import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Pencil } from 'lucide-react';
import { getLedger } from '@/lib/ipdApi';
import { format } from 'date-fns';

// ─── Status ────────────────────────────────────────────────────────────────

export const ADMISSION_STATUSES = ['admitted', 'discharged', 'lama', 'absconded', 'referred_out', 'expired'] as const;
export const DISCHARGE_STATUSES = ADMISSION_STATUSES.filter(s => s !== 'admitted');

export const STATUS_LABELS: Record<string, string> = {
  referred_out: 'Referred Out',
  expired: 'Deceased',
};

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    admitted:     'bg-blue-100 text-blue-700',
    discharged:   'bg-green-100 text-green-700',
    lama:         'bg-amber-100 text-amber-700',
    absconded:    'bg-red-100 text-red-700',
    referred_out: 'bg-purple-100 text-purple-700',
    expired:      'bg-slate-200 text-slate-700',
  };
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
      styles[status] ?? 'bg-muted text-muted-foreground',
    )}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function InfoLine({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-muted-foreground">:</span>
      <span className="font-medium truncate">{value || '—'}</span>
    </div>
  );
}

// ─── Patient & admission info bar (shared across all tabs) ───────────────────

interface PatientAdmissionBarProps {
  admission: any;
  canWrite: boolean;
  onTransferClick: () => void;
  onDischargeClick: () => void;
  onDatesClick: () => void;
}

export function PatientAdmissionBar({ admission, canWrite, onTransferClick, onDischargeClick, onDatesClick }: PatientAdmissionBarProps) {
  return (
    <div className="rounded-md border px-4 py-3">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-4">
        <InfoLine label="Patient Name" value={admission?.patient_name} />
        <InfoLine label="Patient ID" value={admission?.patient_mrn} />
        <InfoLine label="Age / Gender" value={`${admission?.patient_age ?? '—'} / ${admission?.patient_gender ?? '—'}`} />
        <InfoLine label="Phone No" value={admission?.patient_phone} />
        <InfoLine label="IPD No" value={admission?.admission_number} />
        <div className="flex items-center gap-1.5">
          <InfoLine label="Status" value={<StatusBadge status={admission?.status ?? ''} />} />
          {canWrite && (admission?.status === 'admitted' ? (
            <button type="button" onClick={onDischargeClick}
              className="text-xs font-medium leading-none text-primary hover:underline shrink-0">
              Discharge
            </button>
          ) : (
            <button type="button" onClick={onDatesClick} aria-label="Edit discharge details"
              className="text-muted-foreground hover:text-foreground shrink-0">
              <Pencil className="w-3 h-3" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <InfoLine label="Admitted On" value={
            admission?.admission_date ? format(new Date(admission.admission_date), 'dd-MMM-yyyy, hh:mm a') : '—'
          } />
          {canWrite && (
            <button type="button" onClick={onDatesClick} aria-label="Edit dates"
              className="text-muted-foreground hover:text-foreground shrink-0">
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <InfoLine label="Ward / Bed" value={`${admission?.ward_name ?? '—'} / ${admission?.bed_number ?? '—'}`} />
          {canWrite && admission?.status === 'admitted' && (
            <button type="button" onClick={onTransferClick} aria-label="Transfer bed"
              className="text-muted-foreground hover:text-foreground shrink-0">
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Billing totals bar (shared across all tabs) ──────────────────────────────

export function BillingTotalsBar({ admission, refreshKey }: { admission: any; refreshKey?: number }) {
  const [ledger, setLedger] = useState<{ total_billed: number; total_paid: number; balance_due: number } | null>(null);

  useEffect(() => {
    getLedger(admission.id).then(setLedger).catch(console.error);
  }, [admission.id, refreshKey]);

  const billed = ledger ? Number(ledger.total_billed) : 0;
  const paid   = ledger ? Number(ledger.total_paid)   : 0;
  const due    = ledger ? Number(ledger.balance_due)  : 0;

  return (
    <div className="rounded-md border px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-sm font-semibold">Billing Summary</h3>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">Total Billed:{' '}
          <span className="font-semibold text-foreground">₹{billed.toLocaleString('en-IN')}</span>
        </span>
        <span className="text-muted-foreground">Total Paid:{' '}
          <span className="font-semibold text-green-600">₹{paid.toLocaleString('en-IN')}</span>
        </span>
        <span className="text-muted-foreground">Total Due:{' '}
          <span className={cn('font-semibold', due > 0 ? 'text-red-600' : 'text-foreground')}>₹{due.toLocaleString('en-IN')}</span>
        </span>
      </div>
    </div>
  );
}
